import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Snowflake, Play, Tag as TagIcon } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { PublicAPI } from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function PublicClipView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    PublicAPI.clip(token).then(setData).catch((e) => {
      setErr(e?.response?.data?.detail || 'This share link is no longer valid.');
    });
  }, [token]);

  const seekTo = (sec) => {
    if (videoRef.current) {
      videoRef.current.currentTime = sec;
      videoRef.current.play().catch(() => {});
    }
  };

  if (err) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-600 p-6 text-center">{err}</div>;
  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">Loading</div>;

  const { video, player, tags } = data;
  const isYouTube = video.source === 'youtube' && video.youtubeId;
  const streamUrl = !isYouTube ? `${BACKEND_URL}/api/videos/${video.id}/stream` : null;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-clip-view">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
            <Snowflake className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-sm">ShiftNotes<sup className="text-[9px] text-sky-600 ml-0.5">&trade;</sup></div>
            <div className="text-[10px] uppercase tracking-widest text-sky-600/80">Shared Clip</div>
          </div>
          <Badge className="ml-auto bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Read only</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Video */}
        <div className="bg-black rounded-2xl overflow-hidden shadow-sm">
          {isYouTube ? (
            <div className="relative aspect-video">
              <iframe
                title={video.title}
                src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <video
              ref={videoRef}
              src={streamUrl}
              controls
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="public-clip-video"
            />
          )}
        </div>

        {/* Clip meta */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Clip</div>
          <h1 className="font-display text-2xl font-bold" data-testid="public-clip-title">{video.title}</h1>
          {player && (
            <div className="mt-3 flex items-center gap-3">
              {player.avatar ? (
                <img src={player.avatar} alt="" className="h-10 w-10 rounded-full ring-2 ring-slate-200" />
              ) : null}
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {player.firstName} {player.lastName}
                  {player.jerseyNumber ? <span className="text-slate-500 font-mono ml-1.5">#{player.jerseyNumber}</span> : null}
                </div>
                <div className="text-xs text-slate-500">
                  {player.position}{player.team ? `  ${player.team}` : ''}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tagged moments */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TagIcon className="h-4 w-4 text-amber-600" />
            <h2 className="font-display font-semibold">Tagged Moments</h2>
            <span className="text-xs text-slate-500">({tags.length})</span>
          </div>
          {tags.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center">No tagged moments on this clip yet.</div>
          )}
          <ul className="space-y-2" data-testid="public-clip-tags">
            {tags.map(t => (
              <li key={t.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => seekTo(t.timestampSec)}
                    disabled={isYouTube}
                    title={isYouTube ? 'Seek disabled on YouTube preview' : 'Jump to moment'}
                    className="flex items-center gap-1.5 font-mono text-xs text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded px-2 py-1 hover:bg-sky-500/20 disabled:opacity-60">
                    <Play className="h-3 w-3" fill="currentColor" /> {fmt(t.timestampSec)}
                  </button>
                  {t.rating != null && (
                    <Badge className="bg-sky-500/10 text-sky-700 border-sky-500/30 font-mono text-[10px]">{t.rating}/10</Badge>
                  )}
                </div>
                <div className="mt-2 text-sm text-slate-900">{t.label}</div>
                {t.evaluatorName && (
                  <div className="mt-1 text-xs text-slate-500">{t.evaluatorName}</div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-center text-[11px] text-slate-400 py-4">
          ShiftNotes<sup className="text-[8px] text-sky-600">&trade;</sup>  a product of NextShift Development LLC
        </div>
      </main>
    </div>
  );
}
